import { create } from 'zustand'

import { posthog } from '@/lib/posthog'

type InstallEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallReason = 'reminders' | 'win'

const SNOOZE_KEY = 'pwa-install-snoozed-until'
const WINS_KEY = 'pwa-wins'
const SNOOZE_MS = 7 * 86_400_000

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {}
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function installPlatform(): 'ios' | 'android' | null {
  const ua = navigator.userAgent
  // iPadOS reports a desktop Mac UA; touch points give it away.
  if (/iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1))
    return 'ios'
  if (/android/i.test(ua)) return 'android'
  return null
}

// In-app browsers (LINE, Instagram, …) can't install web apps at all.
export function inAppBrowser(): string | null {
  const ua = navigator.userAgent
  if (/\bLine\//i.test(ua)) return 'LINE'
  if (/Instagram/i.test(ua)) return 'Instagram'
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'Facebook'
  if (/Twitter/i.test(ua)) return 'X'
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'TikTok'
  return null
}

type PwaState = {
  deferred: InstallEvent | null
  open: InstallReason | null
  installed: boolean
  snoozedUntil: number
}

export const usePwaStore = create<PwaState>(() => ({
  deferred: null,
  open: null,
  installed: false,
  snoozedUntil: Number(read(SNOOZE_KEY)) || 0,
}))

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    usePwaStore.setState({ deferred: e as InstallEvent })
  })
  window.addEventListener('appinstalled', () => {
    usePwaStore.setState({ deferred: null, open: null, installed: true })
    posthog.capture('pwa_installed')
  })
}

export function useCanAskInstall(): boolean {
  const installed = usePwaStore((s) => s.installed)
  const snoozedUntil = usePwaStore((s) => s.snoozedUntil)
  return canAsk(installed, snoozedUntil)
}

function canAsk(installed: boolean, snoozedUntil: number): boolean {
  return !!installPlatform() && !installed && !isStandalone() && Date.now() > snoozedUntil
}

export function requestInstall(reason: InstallReason) {
  const { installed, snoozedUntil, open } = usePwaStore.getState()
  if (open || !canAsk(installed, snoozedUntil)) return
  usePwaStore.setState({ open: reason })
  posthog.capture('pwa_prompt_shown', {
    reason,
    inApp: inAppBrowser(),
    platform: installPlatform(),
  })
}

export function snoozeInstall() {
  const until = Date.now() + SNOOZE_MS
  write(SNOOZE_KEY, String(until))
  usePwaStore.setState({ open: null, snoozedUntil: until })
}

export async function promptInstall(): Promise<boolean> {
  const { deferred } = usePwaStore.getState()
  if (!deferred) return false
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  posthog.capture('pwa_prompt', { outcome })
  // The event is single-use either way.
  usePwaStore.setState({ deferred: null })
  if (outcome === 'accepted') usePwaStore.setState({ open: null })
  else snoozeInstall()
  return outcome === 'accepted'
}

// Asks after the second completed task: early enough to matter, late enough to have
// seen the app work.
export function noteWin() {
  const wins = (Number(read(WINS_KEY)) || 0) + 1
  write(WINS_KEY, String(wins))
  if (wins >= 2) setTimeout(() => requestInstall('win'), 1500)
}
